import {
  CalendarConfigurationDto,
  CalendarType,
  MiniCalendarType,
  QueryFactory,
} from '@lmt/shared-types';
import { ChangeDateControl } from '@lmt/unicorn-shared/app/components/calendar/change-date/ChangeDateControl';
import { CalendarConfigurationList } from '@lmt/unicorn-shared/app/components/calendar/configuration/list/CalendarConfigurationList';
import { EventFilter } from '@lmt/unicorn-shared/app/components/calendar/filter/EventFilter';
import { SwitchingCalendar } from '@lmt/unicorn-shared/app/components/calendar/mini-calendar/SwitchingCalendar';
import {
  ListType,
  PatientAndWaitingListNavigation,
} from '@lmt/unicorn-shared/app/components/calendar/PatientAndWaitingListNavigation';
import { WaitingList } from '@lmt/unicorn-shared/app/components/calendar/waiting-list/WaitingList';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { UIConfigurationsActions } from '@lmt/unicorn-shared/app/features/ui-configurations/reducer';
import { getUIConfigurations } from '@lmt/unicorn-shared/app/features/ui-configurations/selector';
import ExportIcon from '@lmt/unicorn-shared/app/icons/export-big.svg?react';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { useResetFilters } from '@lmt/unicorn-shared/app/helpers/filter-helper';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { useEventCallback } from '@mui/material';
import { formatISO, getISOWeek, isToday } from 'date-fns';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonColor } from '../common/ui-kit/buttons/Button';
import { isEmpty } from '../measurements/copy-datablock-field/common';

import styles from './CalendarPage.module.scss';
import { Calendar } from './devextreme/Calendar';
import { CalendarOptionsButton } from './options/CalendarOptionsButton';
import { CalendarPatientList } from './patient-list/CalendarPatientList';

const defaultCalendarConfiguration: CalendarConfigurationDto = {} as any;

export const CalendarPage: FC = React.memo(function CalendarPage() {
  const isFixedTab = useTabs().isFixedTab;
  const dispatch = useUniDispatch();
  const [isExportData, setExportData] = useState<boolean>(false);
  const [isExportEnabled, setExportEnabled] = useState<boolean>(false);
  const { data: calendarSettings } =
    QueryFactory.CalendarSettingsQuery.useGetQuery();
  const i18n = useTranslation();

  const defaultConfigurationId =
    useUniSelector(getUIConfigurations).calendarConfigurationId;
  const defaultCalendarViewId = calendarSettings?.defaultCalendarView;

  const { data: configurations } =
    QueryFactory.CalendarConfigurationQuery.useGetCalendarConfigurationsQuery();
  const { queryParams: parsedQueryString, setQueryParams: setQueryString } =
    pages.calendarTab.useParams();

  const configurationId = parsedQueryString.configurationId;

  const handleConfigurationChange = useEventCallback(
    (newConfigurationId: number) => {
      setQueryString({ configurationId: newConfigurationId });
      if (isFixedTab) {
        dispatch(
          UIConfigurationsActions.calendarConfigurationIdChanged(
            newConfigurationId,
          ),
        );
      }
    },
  );

  const { data: configuration, isLoading: isConfigurationLoading } =
    QueryFactory.CalendarConfigurationQuery.useGetCalendarConfigurationQuery(
      configurationId!,
      { enabled: !!configurationId, throwOnError: false },
    );

  useEffect(() => {
    if (!configurations) {
      return;
    }
    const configurationIds = configurations.map((c) => c.id);
    const defaultConfiguration =
      defaultConfigurationId || defaultCalendarViewId;

    if (configurationId && configurationIds?.includes(configurationId)) {
      handleConfigurationChange(configurationId);
    } else {
      if (
        (!configurationId || !configurationIds.includes(configurationId)) &&
        defaultConfiguration &&
        configurationIds.includes(defaultConfiguration)
      ) {
        handleConfigurationChange(defaultConfiguration);
      } else if (configurations && configurations[0]?.id) {
        handleConfigurationChange(configurations[0].id);
      } else {
        handleConfigurationChange(0);
      }
    }
  }, [configurationId, configurations]);

  const calendarType = configuration?.calendarType ?? CalendarType.Week;
  const currentDay = parsedQueryString.currentDay;
  const subtenants: number[] = parsedQueryString.subtenants.filter(
    (subtenant) => subtenant !== null,
  ) as number[];

  const [shownListType, setShownListType] = useState<ListType>('patients');

  const setCurrentDay = useEventCallback((date: Date) => {
    setQueryString({
      currentDay: date,
    });
  });

  useEffect(
    function RegularlyUpdateTodayInUrl() {
      // we need setTimeout to not conflict with `handleConfigurationChange`
      setTimeout(
        () =>
          setQueryString({
            today: new Date(),
          }),
        100,
      );
    },
    [location?.search],
  );

  // useEffect(function ChangeDateToTodayIfTodayDiffers() {
  //   if (!isToday(parsedQueryString.today)) {
  //     console.log(
  //       'changing currentDay to today since today changed',
  //       'todayInUrl:',
  //       parsedQueryString.today,
  //       'today',
  //       formatISO(new Date(), { representation: 'date' }),
  //     );
  //     // we need setTimeout to not conflict with `handleConfigurationChange`
  //     setTimeout(
  //       () =>
  //         setQueryString({
  //           currentDay: new Date(),
  //           today: new Date(),
  //         }),
  //       200,
  //     );
  //   }
  // }, []);

  const setConfigurationQueryParams = useEventCallback(
    (configuration: CalendarConfigurationDto) => {
      const isFilterDisplayed = configuration.isFilterDisplayed;
      setQueryString({
        subtenants:
          (isFilterDisplayed &&
            configuration.hasFilterBySubtenants &&
            configuration.subtenants) ||
          [],
        doctors:
          (isFilterDisplayed &&
            configuration.hasFilterByDoctors &&
            configuration.doctors) ||
          [],
        visitTypes:
          (isFilterDisplayed &&
            configuration.hasFilterByVisitTypes &&
            configuration.visitTypes) ||
          [],
        places:
          (isFilterDisplayed &&
            configuration.hasFilterByPlaces &&
            configuration.places) ||
          [],
        specialConditions:
          (isFilterDisplayed &&
            configuration.hasFilterBySpecialConditions &&
            configuration.specialConditions) ||
          [],
        tags:
          (isFilterDisplayed &&
            configuration.hasFilterByTags &&
            configuration.tags) ||
          [],
        visitStatuses:
          (isFilterDisplayed &&
            configuration.hasFilterByVisitStatuses &&
            configuration.visitStatuses) ||
          [],
        isFilterDisplayed: configuration.isFilterDisplayed,
      });
    },
  );

  const needToRemoveFilters = useResetFilters(
    configuration,
    setConfigurationQueryParams,
    setConfigurationQueryParams,
  );

  const showFilters = configuration?.isFilterDisplayed;
  const showPatients = configuration?.isShowingPatients;
  const miniCalendarType =
    configuration?.miniCalendarType ?? MiniCalendarType.OneMonth;
  const showRoomOccupancy =
    configuration?.isShowingRoomOccupancy &&
    configuration.isResourceViewCalendar;
  const selectedPlaces = configuration?.places;
  const showSelectedRoomOccupancy =
    configuration?.isShowingSelectedRoomOccupancy && !isEmpty(selectedPlaces);

  const showWaitingList = configuration?.isShowingWaitingList;

  const showNavigationBar = showPatients && showWaitingList;

  const displayWeekNumberInHeader =
    (calendarType === CalendarType.Week || calendarType === CalendarType.Day) &&
    configuration?.displayWeekNumber;
  const displayWeekNumberInMonthView =
    configuration?.calendarType === CalendarType.Month &&
    configuration?.displayWeekNumber;

  return (
    <div className={styles.pageContainer} data-test-id={'calendar-page'}>
      {(showPatients || showWaitingList) && (
        <div className={styles.patientPanel}>
          {showNavigationBar ? (
            <div
              className={styles.itemsList}
              data-test-id={'calendar-items-list'}
            >
              <PatientAndWaitingListNavigation
                shownListType={shownListType}
                changeListType={setShownListType}
              />
              {shownListType === 'patients' && (
                <CalendarPatientList
                  subtenants={subtenants}
                  disableTitle={true}
                />
              )}
              {shownListType === 'waitingList' && (
                <WaitingList subtenants={subtenants} disableTitle={true} />
              )}
            </div>
          ) : (
            <div
              className={styles.itemsList}
              data-test-id={'calendar-items-list'}
            >
              {showPatients && <CalendarPatientList subtenants={subtenants} />}
              {showWaitingList && <WaitingList subtenants={subtenants} />}
            </div>
          )}
        </div>
      )}
      <div className={styles.calendarPanel}>
        <div className={styles.header} data-test-id={'header'}>
          <div className={styles.headerStart} data-test-id={'header-date'}>
            {localFormat(currentDay, 'MMMM yyyy')}
          </div>
          <div className={styles.headerEnd}>
            <ChangeDateControl
              currentDay={currentDay}
              setCurrentDay={setCurrentDay}
              calendarType={calendarType}
            />
            <CalendarConfigurationList
              onConfigurationChange={handleConfigurationChange}
              configurations={configurations}
              currentConfigurationId={configurationId}
            />
            <Button
              disabled={
                isExportData || !isExportEnabled || isConfigurationLoading
              }
              iconComponent={<ExportIcon />}
              color={ButtonColor.Primary}
              className={styles.exportButton}
              onClick={() => setExportData(true)}
              testId="export-button"
            />
            <CalendarOptionsButton />
          </div>
        </div>
        <div className={styles.mainContainer}>
          <div className={styles.calendarContainer}>
            {displayWeekNumberInHeader && (
              <div
                className={styles.weekNumberHeader}
                data-test-id="week-number-header"
              >
                {i18n.t('calendar.resource_view.week_number', {
                  number: getISOWeek(currentDay),
                })}
              </div>
            )}
            <div
              className={styles.mainBody}
              data-display-week-number={displayWeekNumberInMonthView}
            >
              <Calendar
                key={configuration?.id}
                containerClass={styles.calendar}
                currentDate={currentDay}
                calendarType={calendarType}
                configuration={configuration ?? defaultCalendarConfiguration}
                isConfigurationLoading={isConfigurationLoading}
                isExportData={isExportData}
                setExportData={setExportData}
                setExportEnabled={setExportEnabled}
                setShownListType={setShownListType}
                shownListType={shownListType}
              />
            </div>
          </div>
          {(miniCalendarType != MiniCalendarType.None || showFilters) && (
            <div className={styles.filtersAndDataPickerPanel}>
              {miniCalendarType != MiniCalendarType.None && (
                <SwitchingCalendar
                  onDateChange={setCurrentDay}
                  selectedDate={currentDay}
                  mainCalendarType={calendarType}
                  isShowingOccupancy={showRoomOccupancy}
                  places={selectedPlaces}
                  isShowingSelectedOccupancy={showSelectedRoomOccupancy}
                  miniCalendarType={miniCalendarType}
                  displayWeekNumber={configuration?.displayWeekNumber}
                  showDaysOutsideCurrentMonth={true}
                />
              )}
              {showFilters && (
                <EventFilter
                  configuration={configuration}
                  setConfigurationQueryParams={setConfigurationQueryParams}
                  needToRemoveFiltersOnConfigurationChange={
                    needToRemoveFilters.current
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
